import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Checkbox,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css"; // Main style file
import "react-date-range/dist/theme/default.css"; // Theme CSS file
import { changeDateFormat } from "../../utils/utility";
import { useDarkMode } from "../context/darkModeContext";
import "./filterModal.css";

const FilterModal = ({
  open,
  onClose,
  options = [],
  selectedValues = [],
  onSave,
  currentSortOrder,
  filterKey,
  hasMore,
  displayKey,
  id,
  fetchApi,
  searchKey,
  type,
  placeholderKey,
}) => {
  const { colors } = useDarkMode();
  const [localSelection, setLocalSelection] = useState(selectedValues || []);
  const [sortOrder, setSortOrder] = useState(currentSortOrder);
  const [searchValue, setSearchValue] = useState("");
  const [optionList, setOptionList] = useState([]);
  const clientId = localStorage.getItem("client_id");
  const [page, setPage] = useState(1);
  const limit = 15;
  const currentDate = new Date();
  const [selectionRange, setSelectionRange] = useState({
    startDate: null, // new Date(currentDate.setMonth(currentDate.getMonth() - 2)),
    endDate: null, // new Date(new Date().setDate(new Date().getDate() + 2)),
    key: "selection",
  });

  const [numberRange, setNumberRange] = useState({ min: "", max: "" });

  // Refs for focusing textfields
  const searchInputRef = useRef(null);
  const minInputRef = useRef(null);

  const getSortLabels = () => {
    if (type === "date-range") {
      return { asc: "Old to New", desc: "New to Old" };
    } else if (type === "number-range") {
      return { asc: "Low to High", desc: "High to Low" };
    } else {
      return { asc: "A to Z", desc: "Z to A" };
    }
  };

  const sortLabels = getSortLabels();

  useEffect(() => {
    if (open) {
      if (type && type === "date-range") {
        setSelectionRange(selectedValues);
      } else if (type === "number-range") {
        setNumberRange(selectedValues || { min: "", max: "" });
        // Focus on min input for number-range
        setTimeout(() => {
          if (minInputRef.current) {
            minInputRef.current.focus();
          }
        }, 100);
      } else {
        setLocalSelection(selectedValues || []);
        // Focus on search input for other types
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 100);
      }
      setSortOrder(currentSortOrder);
      setSearchValue(""); // Reset search value when modal opens
    }
  }, [open, selectedValues, currentSortOrder, type]);

  useEffect(() => {
    setSearchValue(searchKey);
  }, [searchKey]);

  const handleCheckboxChange = (optionId) => {
    if (["All", "all"].includes(optionId)) {
      if (localSelection.includes(optionId)) {
        setLocalSelection([]);
      } else {
        setLocalSelection(optionList.map((x) => x[id]));
      }
    } else {
      setLocalSelection((prevSelection) => {
        if (prevSelection.includes(optionId)) {
          return prevSelection.filter((id) => id !== optionId);
        } else {
          return [...prevSelection, optionId];
        }
      });
    }
    if (localSelection.find((x) => ["all", "All"].includes(x))) {
      setLocalSelection((prev) =>
        prev.filter((x) => !["all", "All"].includes(x)),
      );
    }
  };

  const handleSortChange = (order) => {
    if (sortOrder == order) setSortOrder(0);
    else setSortOrder(order);
  };

  // const handleSave = () => {
  //   onSave(
  //     type == "date-range"
  //       ? {
  //           ...selectionRange,
  //           startDate: changeDateFormat(selectionRange.startDate),
  //           endDate: changeDateFormat(selectionRange.endDate),
  //         }
  //       : localSelection,
  //     filterKey,
  //     sortOrder,
  //     searchValue
  //   ); // Save the selected values
  //   onClose(); // Close the modal after saving
  // };

  const handleSave = () => {
    if (type === "date-range") {
      onSave(
        selectionRange.startDate && selectionRange.endDate
          ? {
              startDate: changeDateFormat(selectionRange.startDate),
              endDate: changeDateFormat(selectionRange.endDate),
            }
          : { startDate: null, endDate: null },
        filterKey,
        sortOrder,
        searchValue,
      );
    } else if (type === "number-range") {
      onSave(
        { min: numberRange.min, max: numberRange.max },
        filterKey,
        sortOrder,
        searchValue,
      );
    } else {
      onSave(localSelection, filterKey, sortOrder, searchValue);
    }
    onClose();
  };

  const clearFilter = () => {
    if (type === "date-range")
      onSave({ startDate: null, endDate: null }, filterKey, 0, "");
    else if (type === "number-range")
      onSave({ min: "", max: "" }, filterKey, 0, "");
    else onSave([], filterKey, 0, "");
    onClose();
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    setPage(1);
  };

  useEffect(() => {
    if (options && options.length)
      setOptionList([
        { [id]: "All", [displayKey]: "All" },
        ...options.filter((x) =>
          x[displayKey]
            .toLowerCase()
            .includes((searchValue || "").toLowerCase()),
        ),
      ]);
  }, [options, searchValue]);

  useEffect(() => {
    if (fetchApi && type != "date-range" && type != "number-range") {
      fetchApi(searchValue, clientId, limit, (page - 1) * limit).then((res) => {
        if (res.data) {
          if (page == 1) {
            setOptionList([{ [id]: "All", [displayKey]: "All" }, ...res.data]);
          } else {
            setOptionList([...optionList, ...res.data]);
          }
        }
      });
    }
  }, [fetchApi, searchValue, page]);

  const handleSelect = (ranges) => {
    setSelectionRange(ranges.selection);
  };
  function formatKeyToReadableText(key) {
    if (!key) return ""; // Handle empty input gracefully

    return key
      .split(".") // Split the key by dot notation
      .map((word) => {
        // Split words containing underscores, capitalize them, and join with a space
        return word
          .split("_") // Split by underscores
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1)) // Capitalize each part
          .join(" "); // Join the parts back with a space
      })
      .reverse() // Reverse the array for "Name in Shipping Address"
      .map((word, index) => (index === 0 ? word : `in ${word}`)) // Add "in" where appropriate
      .join(" "); // Join the words to create the final string
  }

  return (
    open && (
      <Box
        sx={{
          width: 350,
          bgcolor: colors.bgPaper,
          p: 3,
          borderRadius: 2,
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          color: colors.textPrimary,
          "& .rdrCalendarWrapper": {
            backgroundColor: colors.bgPaper,
            color: colors.textPrimary,
          },
          "& .rdrDateDisplayWrapper": {
            backgroundColor: colors.bgPaper,
            color: colors.textPrimary,
          },
          "& .rdrDateInput & .rdrDateDisplayItem & .rdrDateDisplayItemActive": {
            backgroundColor: colors.bgPaper,
            borderColor: colors.border,
            color: colors.textPrimary,
          },
          "& .rdrMonth": {
            color: colors.textPrimary,
            backgroundColor: colors.bgPaper,
          },
          "& .rdrDayNumber": {
            color: colors.textPrimary,
          },
          "& .rdrDayNumber span": {
            color: colors.textPrimary,
          },
          "& .rdrMonthName": {
            color: colors.textPrimary,
          },
          "& .rdrWeekDay": {
            color: colors.textTertiary,
          },
          "& .rdrWeekLine": {
            borderColor: colors.border,
          },
          "& .rdrDays": {
            borderColor: colors.border,
          },
          "& .rdrDayStartPreview": {
            borderColor: colors.border,
            backgroundColor: colors.bgHighlight,
          },
          "& .rdrDayInPreview": {
            backgroundColor: colors.bgHighlight,
          },
          "& .rdrDayEndPreview": {
            borderColor: colors.border,
            backgroundColor: colors.bgHighlight,
          },
          "& .rdrDayStartOfMonth, & .rdrDayEndOfMonth": {
            backgroundColor: colors.bgPaper,
          },
          "& .rdrDayDisabled": {
            backgroundColor: colors.bgPaper,
            color: colors.textSecondary,
          },
          "& .rdrDayPassive": {
            color: colors.textSecondary,
          },
          "& .rdrMonthPicker": {
            color: colors.textPrimary,
            backgroundColor: colors.bgPaper,
          },
          "& .rdrYearPicker": {
            color: colors.textPrimary,
            backgroundColor: colors.bgPaper,
          },
          "& .rdrInput": {
            backgroundColor: colors.bgPaper,
            color: colors.textPrimary,
            borderColor: colors.border,
          },
          "& .rdrInputWrapper": {
            borderColor: colors.border,
          },
          "& .rdrButton": {
            color: colors.textPrimary,
          },
          "& .rdrStartEdge": {
            borderColor: colors.primary,
          },
          "& .rdrEndEdge": {
            borderColor: colors.primary,
          },
          "& .rdrSelected": {
            backgroundColor: colors.primary,
          },
          "& .rdrInRange": {
            backgroundColor: colors.bgHighlight,
            color: colors.textPrimary,
          },
          "& .rdrDayToday .rdrDayNumber span::after": {
            backgroundColor: colors.primary,
          },
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems={"start"}
          mb={2}
          gap={2}
        >
          <Button
            onClick={() => handleSortChange(1)}
            size="small"
            style={{ width: 150, fontFamily: "Poppins", fontSize: 13 }}
            variant={sortOrder === 1 ? "contained" : "outlined"}
            startIcon={<ArrowUpwardIcon />}
          >
            {sortLabels.asc}
          </Button>
          <Button
            onClick={() => handleSortChange(-1)}
            size="small"
            style={{ width: 150, fontFamily: "Poppins", fontSize: 13 }}
            variant={sortOrder === -1 ? "contained" : "outlined"}
            startIcon={<ArrowDownwardIcon />}
          >
            {sortLabels.desc}
          </Button>
        </Box>

        {type === "date-range" ? (
          //  <DateRange
          //   ranges={[selectionRange]}
          //   onChange={handleSelect}
          //   editableDateInputs={false}
          //   moveRangeOnFirstSelection={false}
          //   startDatePlaceholder="From"
          //   endDatePlaceholder="To"
          //   />
          <DateRange
            editableDateInputs={true}
            moveRangeOnFirstSelection={false}
            ranges={[
              selectionRange &&
              selectionRange.startDate &&
              selectionRange.endDate
                ? {
                    startDate: selectionRange.startDate,
                    endDate: selectionRange.endDate,
                    key: "selection",
                  }
                : {
                    startDate: new Date(),
                    endDate: new Date(),
                    key: "selection",
                  },
            ]}
            onChange={(item) => {
              if (item?.selection) {
                setSelectionRange({
                  startDate: item.selection.startDate,
                  endDate: item.selection.endDate,
                });
              }
            }}
          />
        ) : type === "number-range" ? (
          <Box display="flex" gap={2} mt={1}>
            <TextField
              label="Min"
              type="number"
              size="small"
              fullWidth
              value={numberRange.min}
              onChange={(e) =>
                setNumberRange({ ...numberRange, min: e.target.value })
              }
              inputRef={minInputRef}
            />
            <TextField
              label="Max"
              type="number"
              size="small"
              fullWidth
              value={numberRange.max}
              onChange={(e) =>
                setNumberRange({ ...numberRange, max: e.target.value })
              }
            />
          </Box>
        ) : (
          <div style={{ overflowY: "auto" }}>
            <TextField
              fullWidth
              placeholder={`Search by  ${
                placeholderKey || formatKeyToReadableText(filterKey)
              }`}
              value={searchValue}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ maxHeight: "20vh", overflowY: "auto" }}>
              {optionList.map((option, index) => (
                <Box
                  mt={1}
                  key={"option-" + option[id] + "-" + index}
                  display="flex"
                  alignItems="center"
                  mb={1}
                  justifyContent={"flex-start"}
                  onClick={() => handleCheckboxChange(option[id])}
                  sx={{ cursor: "pointer" }}
                >
                  <Checkbox
                    style={{ padding: 2 }}
                    checked={localSelection.includes(option[id])}
                    // onChange={() => handleCheckboxChange(option[id])}
                  />
                  <Tooltip title={option[displayKey]} arrow>
                    <Typography
                      fontFamily={"Poppins"}
                      fontSize={13}
                      noWrap
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 250,
                      }}
                    >
                      {option[displayKey]}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}

              {hasMore &&
              (optionList.length - 1) % limit === 0 &&
              optionList.length - 1 === limit * page ? (
                <Box
                  key={`load-more`}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mb={1}
                >
                  <Button onClick={() => setPage(page + 1)} variant="outlined">
                    Load More
                  </Button>
                </Box>
              ) : null}
            </Box>
          </div>
        )}

        <Box
          paddingBottom={1}
          display="flex"
          justifyContent="flex-end"
          gap={2}
          marginTop={3}
        >
          <Button
            onClick={onClose}
            style={{ textTransform: "none" }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={clearFilter}
            style={{ textTransform: "none" }}
            variant="outlined"
          >
            Clear Filter
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            style={{ textTransform: "none" }}
          >
            Ok
          </Button>
        </Box>
      </Box>
    )
  );
};

export default FilterModal;
